import { ImageIcon, Loader2, UploadCloud, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { useMediaUpload } from '../lib';

import { useMediaFolder } from './media-folder';
import { resolveMedia } from './media.api';

type MediaKind = 'image' | 'video' | 'document';

interface MediaDropFieldProps {
  /** Stored media id (the value persisted on the form/section). */
  value: string | null | undefined;
  onChange: (mediaId: string | null) => void;
  label?: string;
  kind?: MediaKind;
  /** Accept attribute, e.g. "image/*". Defaults by kind. */
  accept?: string;
  folder?: string;
  disabled?: boolean;
  /** Compact square (e.g. icon pickers) vs wide banner zone. */
  variant?: 'banner' | 'square';
}

const ACCEPT_BY_KIND: Record<MediaKind, string> = {
  image: 'image/*',
  video: 'video/*',
  document: '.pdf,.doc,.docx',
};

// Checkerboard behind image previews so transparent / white icons (e.g. the
// white-on-navy timeline badges) are visible instead of blending into a white
// box. The two greys give enough contrast for both light and dark artwork.
const CHECKER: React.CSSProperties = {
  backgroundColor: '#e2e8f0',
  backgroundImage:
    'linear-gradient(45deg,#94a3b8 25%,transparent 25%),linear-gradient(-45deg,#94a3b8 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#94a3b8 75%),linear-gradient(-45deg,transparent 75%,#94a3b8 75%)',
  backgroundSize: '18px 18px',
  backgroundPosition: '0 0,0 9px,9px -9px,-9px 0',
};

/**
 * Inline drag-and-drop upload field. Replaces the old "upload in a library →
 * copy URL → paste" flow: the file is uploaded directly here (3-step presigned
 * S3 flow via useMediaUpload) and the resulting media id is stored on the form.
 * Shows a live preview of the current value, upload progress, and a remove.
 */
export const MediaDropField = ({
  value,
  onChange,
  label,
  kind = 'image',
  accept,
  folder,
  disabled,
  variant = 'banner',
}: MediaDropFieldProps): React.JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const contextFolder = useMediaFolder();
  const uploadFolder = folder ?? contextFolder;
  const { upload, status, progress, error, reset } = useMediaUpload<{ id: string }>();

  // Resolve an existing value → preview URL.
  useEffect(() => {
    let cancelled = false;
    if (!value) {
      setPreviewUrl(null);
      return;
    }
    // Clear any stale preview immediately so a value change (e.g. reordering
    // items in a Repeater, which reuses this instance by index) never shows the
    // previous item's image under the new value while the new one resolves.
    setPreviewUrl(null);
    setResolving(true);
    resolveMedia(value)
      .then((m) => !cancelled && setPreviewUrl(m.url))
      .catch(() => !cancelled && setPreviewUrl(null))
      .finally(() => !cancelled && setResolving(false));
    return () => {
      cancelled = true;
    };
  }, [value]);

  const handleFile = async (file: File): Promise<void> => {
    if (disabled) return;
    reset();
    try {
      const asset = await upload(file, {
        kind: kind.toUpperCase() as 'IMAGE' | 'VIDEO' | 'DOCUMENT',
        folder: uploadFolder,
      });
      if (asset?.id) onChange(asset.id);
    } catch {
      /* error surfaced via `error` */
    }
  };

  const onDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const uploading = status === 'initiating' || status === 'uploading' || status === 'finalizing';
  // `progress` is a 0-1 fraction; show whole percent.
  const pct = Math.min(100, Math.max(0, Math.round(progress * 100)));
  const isSquare = variant === 'square';
  const zoneSize = isSquare ? 'h-28 w-28' : 'min-h-[160px] w-full';

  return (
    <div>
      {label && (
        <label className="mb-1.5 block text-xs font-medium text-(--fg-muted)">{label}</label>
      )}

      {value && previewUrl ? (
        <div
          className={`group relative overflow-hidden rounded-lg border border-(--border) bg-(--z-50) ${isSquare ? 'h-28 w-28' : ''}`}
          style={kind === 'image' ? CHECKER : undefined}
        >
          {kind === 'image' ? (
            <img
              src={previewUrl}
              alt=""
              className={isSquare ? 'h-28 w-28 object-cover' : 'max-h-64 w-full object-contain'}
            />
          ) : kind === 'video' ? (
            <video
              src={previewUrl}
              controls
              muted
              playsInline
              preload="metadata"
              className={isSquare ? 'h-28 w-28 object-cover' : 'max-h-64 w-full'}
            />
          ) : (
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="text-primary flex items-center gap-2 p-4 text-sm font-medium underline underline-offset-2 hover:opacity-80"
            >
              <ImageIcon className="h-4 w-4" /> View uploaded file
            </a>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                reset();
              }}
              className="absolute top-2 right-2 rounded-md bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
              aria-label="Remove file"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`flex ${zoneSize} flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
            dragOver
              ? 'border-(--primary) bg-(--accent)'
              : 'border-(--border-strong) bg-(--z-50) hover:border-(--primary)'
          } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin text-(--primary)" />
              <span className="text-xs text-(--fg-muted)">
                {status === 'finalizing' ? 'Finalizing…' : `Uploading… ${pct}%`}
              </span>
              <div className="h-1 w-3/4 overflow-hidden rounded-full bg-(--border)">
                <div
                  className="h-full bg-(--primary) transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </>
          ) : resolving ? (
            <Loader2 className="h-5 w-5 animate-spin text-(--fg-faint)" />
          ) : (
            <>
              <UploadCloud className="h-6 w-6 text-(--fg-faint)" />
              {!isSquare && (
                <span className="text-sm text-(--fg-muted)">
                  Drag &amp; drop or <span className="font-medium text-(--primary)">browse</span>
                </span>
              )}
              <span className="text-[12px] text-(--fg-faint)">{kind} upload</span>
            </>
          )}
        </button>
      )}

      {error && <p className="mt-1 text-xs text-(--ds-error)">Upload failed. Please try again.</p>}

      <input
        ref={inputRef}
        type="file"
        accept={accept ?? ACCEPT_BY_KIND[kind]}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
};
